import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Shield, LogOut, Eye, Trash2, Download, Search, Mail, Phone, MapPin, Globe, MessageCircle, ArrowLeft, X, AlertTriangle, Gift, CheckCircle, Database } from "lucide-react";
import api, { apiError, API_BASE } from "@/lib/api";
import { isTopActive } from "@/lib/moderation";

// Single-file admin app (login + dashboard + listings + contacts + chats).
// Auth: POST /api/auth/login → cookie + bearer token; stored in localStorage["stb_admin_token"].

const TOKEN_KEY = "stb_admin_token";

function setAdminHeader(token) {
  if (token) api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  else delete api.defaults.headers.common["Authorization"];
}

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [me, setMe] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => { setAdminHeader(token); }, [token]);

  // On boot, verify token & load /me
  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        const u = data?.user || data;
        if (u?.role !== "admin") {
          setError("Tento účet nemá administrátorská oprávnění.");
          localStorage.removeItem(TOKEN_KEY);
          setToken("");
        } else {
          setMe(u);
        }
      } catch (e) {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
      }
    })();
  }, [token]);

  const onLogin = async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      const t = data.access_token || data.token;
      if (!t) throw new Error("Server nevrátil token");
      localStorage.setItem(TOKEN_KEY, t);
      setToken(t);
    } catch (e) {
      setError(apiError(e));
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setAdminHeader(null);
    setToken("");
    setMe(null);
  };

  if (!token || !me) {
    return <AdminLogin onLogin={onLogin} error={error} />;
  }

  return <AdminDashboard me={me} onLogout={logout} />;
}

// ----------------------- LOGIN ----------------------- //

function AdminLogin({ onLogin, error }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    await onLogin(email, password);
    setBusy(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" data-testid="admin-login">
      <div className="stb-card p-6 w-full max-w-md">
        <button
          onClick={() => navigate("/")}
          className="stb-btn stb-btn-ghost text-white !p-2 mb-2 text-xs"
        >
          <ArrowLeft size={12} /> zpět na STAVBOŠ
        </button>
        <h1 className="stb-h text-2xl text-white inline-flex items-center gap-2">
          <Shield size={20} className="text-[#FF6B00]" /> Admin přístup
        </h1>
        <p className="text-xs text-[#A3A3A3] mt-1">
          Pouze pro administrátora STAVBOŠ. Všechny přístupy se zaznamenávají.
        </p>
        <form onSubmit={submit} className="mt-4 space-y-3">
          <input
            type="email" className="stb-input" placeholder="E-mail" required
            value={email} onChange={(e) => setEmail(e.target.value)}
            data-testid="admin-email-input"
            autoComplete="email"
          />
          <input
            type="password" className="stb-input" placeholder="Heslo" required
            value={password} onChange={(e) => setPassword(e.target.value)}
            data-testid="admin-password-input"
            autoComplete="current-password"
          />
          {error && <p className="text-[#FF6B00] text-sm" data-testid="admin-login-error">{error}</p>}
          <button className="stb-btn stb-btn-primary w-full" disabled={busy} data-testid="admin-login-submit">
            {busy ? "Přihlašuji…" : "Přihlásit"}
          </button>
        </form>
      </div>
    </div>
  );
}

// ----------------------- DASHBOARD ----------------------- //

function AdminDashboard({ me, onLogout }) {
  const [tab, setTab] = useState("stats");
  const [detail, setDetail] = useState(null); // { kind: "listing" | "chat", id }

  return (
    <div className="min-h-screen text-white">
      <header className="bg-[#0E0E0E] border-b-2 border-[#262626] sticky top-0 z-30">
        <div className="stb-container py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Shield className="text-[#FF6B00]" size={20} />
            <div>
              <h1 className="stb-h text-lg leading-none">STAVBOŠ · Admin</h1>
              <p className="stb-mono text-[10px] text-[#A3A3A3]">{me.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a href="/" className="stb-btn stb-btn-ghost text-white text-xs">na STAVBOŠ</a>
            <button onClick={onLogout} className="stb-btn stb-btn-secondary text-xs" data-testid="admin-logout">
              <LogOut size={14} /> Odhlásit
            </button>
          </div>
        </div>
        <div className="stb-container flex gap-1 overflow-x-auto pb-2">
          {[
            { k: "stats", label: "Přehled" },
            { k: "listings", label: "Inzeráty" },
            { k: "contacts", label: "Kontaktní formuláře" },
            { k: "chats", label: "Chaty" },
            { k: "winners", label: "Výherci 🍺" },
            { k: "tools", label: "Nástroje" },
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => { setDetail(null); setTab(t.k); }}
              className={`stb-segment whitespace-nowrap ${tab === t.k ? "stb-segment-active" : ""}`}
              data-testid={`admin-tab-${t.k}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </header>

      <main className="stb-container py-5">
        {detail?.kind === "listing" && (
          <ListingDetail id={detail.id} onClose={() => setDetail(null)} />
        )}
        {detail?.kind === "chat" && (
          <ChatDetail roomId={detail.id} onClose={() => setDetail(null)} />
        )}

        {!detail && tab === "stats" && <StatsTab />}
        {!detail && tab === "listings" && <ListingsTab onOpen={(id) => setDetail({ kind: "listing", id })} />}
        {!detail && tab === "contacts" && <ContactsTab />}
        {!detail && tab === "chats" && <ChatsTab onOpen={(roomId) => setDetail({ kind: "chat", id: roomId })} />}
        {!detail && tab === "winners" && <WinnersTab />}
        {!detail && tab === "tools" && <ToolsTab />}
      </main>

      <div className="stb-container pb-8">
        <div className="stb-card-static p-3 text-xs text-[#A3A3A3] inline-flex items-center gap-2">
          <AlertTriangle size={14} className="text-[#FFB800]" />
          Každý tvůj přístup se zaznamenává do <code className="stb-mono">admin_access_log</code>. Údaje uchovávej v souladu s GDPR.
        </div>
      </div>
    </div>
  );
}

// ----------------------- STATS ----------------------- //

function StatsTab() {
  const [s, setS] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get("/admin/stats").then((r) => setS(r.data)).catch((e) => setErr(apiError(e)));
  }, []);
  if (err) return <p className="text-[#FF6B00]" data-testid="stats-error">{err}</p>;
  if (!s) return <p className="stb-label">Načítám…</p>;

  const cells = [
    ["Inzeráty (aktivní)", s.listings_total],
    ["Inzeráty (smazané)", s.listings_deleted],
    ["TOP aktivních", s.listings_top_active],
    ["Kontaktní formuláře", s.contact_messages],
    ["Chat rooms", s.chats],
    ["Chat zprávy", s.chat_messages],
    ["Platby (Stripe)", s.payments],
    ["Platby (zaplacené)", s.payments_paid],
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="stats-grid">
      {cells.map(([label, v]) => (
        <div key={label} className="stb-card-static p-4">
          <p className="stb-label">{label}</p>
          <p className="stb-h text-4xl text-[#FF6B00] mt-1">{v}</p>
        </div>
      ))}
    </div>
  );
}

// ----------------------- LISTINGS ----------------------- //

function ListingsTab({ onOpen }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = async (term = q) => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.get("/admin/listings", { params: { q: term, limit: 80 } });
      setItems(data.items); setTotal(data.total);
    } catch (e) { setErr(apiError(e)); } finally { setBusy(false); }
  };

  useEffect(() => { load(""); /* initial */ /* eslint-disable-next-line */ }, []);

  return (
    <div data-testid="admin-listings-tab">
      <form
        onSubmit={(e) => { e.preventDefault(); load(q); }}
        className="flex gap-2 mb-3"
      >
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A3A3A3]" size={14} />
          <input className="stb-input pl-9" placeholder="Hledat: text, e-mail, telefon, IP, ID, VIN…"
            value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-listings-search" />
        </div>
        <button className="stb-btn stb-btn-primary" disabled={busy}>{busy ? "…" : "Hledat"}</button>
      </form>
      <p className="stb-label mb-2">Nalezeno: {total}</p>
      {err && <p className="text-[#FF6B00]">{err}</p>}
      <div className="space-y-2">
        {items.map((l) => (
          <button
            key={l.id}
            onClick={() => onOpen(l.id)}
            className="stb-card p-3 w-full text-left flex gap-3"
            data-testid={`admin-listing-row-${l.id}`}
          >
            {l.photos?.[0] ? (
              <img src={l.photos[0]} alt="" className="w-20 h-20 object-cover border border-[#262626] flex-shrink-0" />
            ) : (
              <div className="w-20 h-20 bg-[#0E0E0E] border border-[#262626] flex items-center justify-center text-[#4A4A4A] stb-mono text-[10px] flex-shrink-0">BEZ FOTO</div>
            )}
            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-2">
                <h3 className="stb-h text-base text-white truncate">{l.title}</h3>
                <span className="stb-mono text-xs text-[#A3A3A3]">{shortDate(l.created_at)}</span>
              </div>
              <p className="text-xs text-[#A3A3A3] line-clamp-1 mt-1">{l.description}</p>
              <div className="flex flex-wrap gap-1 mt-2 text-xs">
                <span className="stb-chip"><Mail size={10} /> {l.owner_email}</span>
                {l.contact_phone && <span className="stb-chip"><Phone size={10} /> {l.contact_phone}</span>}
                {l.location && <span className="stb-chip"><MapPin size={10} /> {l.location}</span>}
                {l.audit?.ip && <span className="stb-chip text-[#FF6B00] border-[#FF6B00]"><Globe size={10} /> {l.audit.ip}</span>}
                {l.status === "deleted" && <span className="stb-chip text-[#EF4444] border-[#EF4444]">smazáno</span>}
                {isTopActive(l) && <span className="stb-chip text-[#FFB800] border-[#FFB800]">TOP</span>}
                {l.is_top && !isTopActive(l) && (
                  <span className="stb-chip text-[#A3A3A3] border-[#525252]">TOP vypršel</span>
                )}
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function ListingDetail({ id, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/admin/listings/${id}`).then((r) => setData(r.data)).catch((e) => setErr(apiError(e)));
  }, [id]);

  const onDelete = async () => {
    if (!window.confirm("Opravdu smazat tento inzerát jako admin?")) return;
    try {
      await api.post(`/admin/listings/${id}/delete`);
      onClose();
    } catch (e) { alert(apiError(e)); }
  };

  const exportUrl = `${API_BASE}/admin/export/listing/${id}`;

  if (err) return <p className="text-[#FF6B00]">{err}</p>;
  if (!data) return <p className="stb-label">Načítám…</p>;
  const l = data.listing;

  return (
    <div data-testid="admin-listing-detail" className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <button onClick={onClose} className="stb-btn stb-btn-ghost text-white"><ArrowLeft size={14} /> Zpět</button>
        <div className="flex gap-2">
          <a className="stb-btn stb-btn-secondary text-xs" href={`/inzerat/${id}`} target="_blank" rel="noreferrer">
            <Eye size={14} /> Otevřít veřejně
          </a>
          <a className="stb-btn stb-btn-secondary text-xs" href={exportUrl} target="_blank" rel="noreferrer"
             data-testid="admin-export-listing">
            <Download size={14} /> Export pro PČR
          </a>
          {l.status !== "deleted" && (
            <button onClick={onDelete} className="stb-btn stb-btn-secondary text-xs text-[#EF4444]"
              data-testid="admin-delete-listing">
              <Trash2 size={14} /> Smazat
            </button>
          )}
        </div>
      </div>

      <section className="stb-card-static p-4">
        <h2 className="stb-h text-2xl text-white">{l.title}</h2>
        <p className="text-[#A3A3A3] text-sm mt-1">{l.description}</p>
        <hr className="stb-divider" />
        <Grid2 items={[
          ["ID", l.id],
          ["Stav", l.status],
          ["Kategorie", `${l.category}${l.subcategory ? " / " + l.subcategory : ""}`],
          ["Cena", l.price_type === "fixed" ? `${l.price} Kč` : l.price_type],
          ["Lokalita", l.location || "—"],
          ["Vytvořeno", l.created_at],
          ["Upraveno", l.updated_at],
          ["Zobrazení", l.views],
          ["Top", isTopActive(l) ? `aktivní do ${l.top_until || "—"}` : (l.is_top ? `prošlé ${l.top_until || ""}` : "—")],
          ["TOP zaplaceno (count)", l.top_paid_count],
          ["VIN", l.vin_code || "—"],
          ["EAN", l.ean_code || "—"],
          ["Výrobní č.", l.serial_number || "—"],
          ["Rok výroby", l.year_of_manufacture || "—"],
          ["Mth", l.machine_hours || "—"],
          ["Váha (kg)", l.weight_kg || "—"],
        ]} />
      </section>

      <section className="stb-card-static p-4">
        <h3 className="stb-label">Inzerent (kontakt + audit)</h3>
        <Grid2 items={[
          ["Jméno", l.contact_name],
          ["E-mail", l.owner_email],
          ["Telefon", l.contact_phone || "—"],
          ["IP adresa při vkládání", l.audit?.ip || "—"],
          ["User-agent", (l.audit?.user_agent || "—").slice(0, 200)],
          ["Manage token (kopírovat opatrně)", l.manage_token],
        ]} />
      </section>

      {l.photos?.length > 0 && (
        <section className="stb-card-static p-4">
          <h3 className="stb-label mb-2">Fotky ({l.photos.length})</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {l.photos.map((p, i) => (
              <a key={i} href={p} target="_blank" rel="noreferrer">
                <img src={p} alt={`foto ${i + 1}`} className="w-full aspect-square object-cover border-2 border-[#262626] hover:border-[#FF6B00]" />
              </a>
            ))}
          </div>
        </section>
      )}

      <section className="stb-card-static p-4">
        <h3 className="stb-label mb-2">Kontaktní formuláře ({data.contact_messages.length})</h3>
        {data.contact_messages.length === 0 ? (
          <p className="text-[#A3A3A3] text-sm">Žádné zprávy</p>
        ) : data.contact_messages.map((m) => (
          <div key={m.id} className="border-t border-[#262626] py-3 first:border-t-0 first:pt-0">
            <div className="flex flex-wrap justify-between gap-2 text-xs">
              <span>
                <strong className="text-white">{m.sender_name}</strong>{" · "}
                <span className="stb-mono">{m.sender_email}</span>
                {m.sender_phone && <span className="stb-mono"> · {m.sender_phone}</span>}
              </span>
              <span className="stb-mono text-[#A3A3A3]">{m.created_at}</span>
            </div>
            {m.offered_price != null && (
              <p className="stb-h text-[#FF6B00] mt-1">Nabídka: {m.offered_price} Kč</p>
            )}
            <p className="text-sm text-white mt-1 whitespace-pre-wrap">{m.message}</p>
            <p className="stb-mono text-[10px] text-[#A3A3A3] mt-1">
              IP: {m.audit?.ip || "—"} · UA: {(m.audit?.user_agent || "").slice(0, 100)}
            </p>
          </div>
        ))}
      </section>

      <section className="stb-card-static p-4">
        <h3 className="stb-label mb-2">Chaty ({data.chats.length})</h3>
        {data.chats.length === 0 ? (
          <p className="text-[#A3A3A3] text-sm">Žádné chaty</p>
        ) : data.chats.map((c) => (
          <div key={c.room_id} className="border-t border-[#262626] py-3 first:border-t-0 first:pt-0">
            <p className="text-sm">
              <strong>{c.buyer_name}</strong> ({c.buyer_email}) · {c.messages_count} zpráv · {shortDate(c.last_message_at)}
            </p>
            <p className="stb-mono text-[10px] text-[#A3A3A3]">
              IP buyer (1.): {c.buyer_audit?.ip || "—"}
            </p>
          </div>
        ))}
      </section>

      <section className="stb-card-static p-4">
        <h3 className="stb-label mb-2">Stripe platby ({data.payments.length})</h3>
        {data.payments.map((p) => (
          <div key={p.session_id} className="border-t border-[#262626] py-2 first:border-t-0 first:pt-0 text-sm">
            <span className="stb-mono text-xs">{p.session_id}</span> · {p.amount} {p.currency} ·{" "}
            <span className={p.payment_status === "paid" ? "text-[#22C55E]" : "text-[#A3A3A3]"}>{p.payment_status}</span>
            {p.credited && " · připsáno"}
            <span className="stb-mono text-xs text-[#A3A3A3] ml-2">{shortDate(p.created_at)}</span>
          </div>
        ))}
      </section>
    </div>
  );
}

// ----------------------- CONTACTS ----------------------- //

function ContactsTab() {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [err, setErr] = useState("");

  const load = async (term = q) => {
    try {
      const { data } = await api.get("/admin/contacts", { params: { q: term, limit: 100 } });
      setItems(data.items); setTotal(data.total);
    } catch (e) { setErr(apiError(e)); }
  };
  useEffect(() => { load(""); /* eslint-disable-next-line */ }, []);

  return (
    <div data-testid="admin-contacts-tab">
      <form onSubmit={(e) => { e.preventDefault(); load(q); }} className="flex gap-2 mb-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A3A3A3]" size={14} />
          <input className="stb-input pl-9" placeholder="Hledat e-mail, telefon, text, IP…"
            value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-contacts-search" />
        </div>
        <button className="stb-btn stb-btn-primary">Hledat</button>
        <a href={`${API_BASE}/admin/export/contacts.csv`} target="_blank" rel="noreferrer"
           className="stb-btn stb-btn-secondary" data-testid="admin-export-contacts">
          <Download size={14} /> CSV
        </a>
      </form>
      <p className="stb-label mb-2">Nalezeno: {total}</p>
      {err && <p className="text-[#FF6B00]">{err}</p>}
      <div className="space-y-2">
        {items.map((m) => (
          <article key={m.id} className="stb-card-static p-3">
            <div className="flex flex-wrap justify-between gap-2 text-xs">
              <span>
                <strong className="text-white">{m.sender_name}</strong>{" · "}
                <a href={`mailto:${m.sender_email}`} className="stb-mono text-[#FF6B00]">{m.sender_email}</a>
                {m.sender_phone && <span className="stb-mono"> · {m.sender_phone}</span>}
              </span>
              <span className="stb-mono text-[#A3A3A3]">{m.created_at}</span>
            </div>
            {m.offered_price != null && (
              <p className="stb-h text-[#FF6B00] mt-1">Nabídka: {m.offered_price} Kč</p>
            )}
            <p className="text-sm text-white mt-1 whitespace-pre-wrap">{m.message}</p>
            <p className="stb-mono text-[10px] text-[#A3A3A3] mt-1">
              IP: <strong className="text-[#FF6B00]">{m.audit?.ip || "—"}</strong> ·{" "}
              UA: {(m.audit?.user_agent || "").slice(0, 120)}
            </p>
            <p className="stb-mono text-[10px] text-[#A3A3A3] mt-1">
              k inzerátu: <a href={`/inzerat/${m.listing_id}`} target="_blank" rel="noreferrer" className="stb-link">{m.listing_id}</a> · prodejce: {m.owner_email}
            </p>
            <button
              type="button"
              className="stb-btn stb-btn-secondary text-xs text-[#EF4444] mt-2"
              onClick={async () => {
                if (!window.confirm("Smazat tuto zprávu?")) return;
                try {
                  await api.post(`/admin/contacts/${m.id}/delete`);
                  load(q);
                } catch (e) { alert(apiError(e)); }
              }}
            >
              <Trash2 size={12} /> Smazat
            </button>
          </article>
        ))}
      </div>
    </div>
  );
}

// ----------------------- CHATS ----------------------- //

function ChatsTab({ onOpen }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [err, setErr] = useState("");

  const load = async (term = q) => {
    try {
      const { data } = await api.get("/admin/chats", { params: { q: term, limit: 100 } });
      setItems(data.items); setTotal(data.total);
    } catch (e) { setErr(apiError(e)); }
  };
  useEffect(() => { load(""); /* eslint-disable-next-line */ }, []);

  return (
    <div data-testid="admin-chats-tab">
      <form onSubmit={(e) => { e.preventDefault(); load(q); }} className="flex gap-2 mb-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A3A3A3]" size={14} />
          <input className="stb-input pl-9" placeholder="Hledat: e-mail, jméno, inzerát, IP…"
            value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-chats-search" />
        </div>
        <button className="stb-btn stb-btn-primary">Hledat</button>
      </form>
      <p className="stb-label mb-2">Nalezeno: {total}</p>
      {err && <p className="text-[#FF6B00]">{err}</p>}
      <div className="space-y-2">
        {items.map((c) => (
          <button key={c.room_id} onClick={() => onOpen(c.room_id)}
            className="stb-card p-3 w-full text-left" data-testid={`admin-chat-row-${c.room_id}`}>
            <div className="flex flex-wrap justify-between gap-2 text-xs">
              <span>
                <MessageCircle size={12} className="inline mr-1 text-[#FF6B00]" />
                <strong className="text-white">{c.buyer_name || "Neznámý"}</strong>
                {" "}({c.buyer_email})
              </span>
              <span className="stb-mono text-[#A3A3A3]">{shortDate(c.last_message_at)}</span>
            </div>
            <p className="text-sm text-[#A3A3A3] mt-1">Inzerát: {c.listing_title} (prodejce: {c.owner_email})</p>
            <p className="stb-mono text-[10px] text-[#A3A3A3] mt-1">
              {c.messages_count} zpráv · buyer IP: <strong className="text-[#FF6B00]">{c.buyer_audit?.ip || "—"}</strong>
            </p>
          </button>
        ))}
      </div>
    </div>
  );
}

function ChatDetail({ roomId, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get(`/admin/chats/${roomId}/messages`).then((r) => setData(r.data)).catch((e) => setErr(apiError(e)));
  }, [roomId]);

  if (err) return <p className="text-[#FF6B00]">{err}</p>;
  if (!data) return <p className="stb-label">Načítám…</p>;
  const r = data.room;

  return (
    <div data-testid="admin-chat-detail" className="space-y-3">
      <div className="flex items-center justify-between">
        <button onClick={onClose} className="stb-btn stb-btn-ghost text-white"><ArrowLeft size={14} /> Zpět</button>
      </div>
      <section className="stb-card-static p-4">
        <h2 className="stb-h text-xl text-white">Chat: {r.listing_title}</h2>
        <Grid2 items={[
          ["Room ID", r.room_id],
          ["Inzerát", r.listing_id],
          ["Kupující", `${r.buyer_name} (${r.buyer_email})`],
          ["Prodejce", r.owner_email || "—"],
          ["Kupující IP (1. návštěva)", r.buyer_audit?.ip || "—"],
          ["Kupující UA (1.)", (r.buyer_audit?.user_agent || "").slice(0, 200)],
          ["Kupující IP (poslední)", r.buyer_last_audit?.ip || "—"],
        ]} />
      </section>
      <section className="stb-card-static p-4">
        <h3 className="stb-label mb-2">Zprávy ({data.messages.length})</h3>
        <div className="space-y-2 max-h-[600px] overflow-y-auto">
          {data.messages.map((m) => (
            <div key={m.id} className={`p-2 border-2 ${m.sender_role === "seller" ? "border-[#FF6B00]" : "border-[#262626]"}`}>
              <div className="flex justify-between text-[10px] stb-mono text-[#A3A3A3]">
                <span>{m.sender_role.toUpperCase()} · {m.sender_id}</span>
                <span>{m.created_at}</span>
              </div>
              {m.type === "text" && <p className="text-sm text-white whitespace-pre-wrap mt-1">{m.text}</p>}
              {m.type === "image" && <img src={m.data_url} alt="" className="max-h-48 mt-1 border border-[#262626]" />}
              {m.type === "audio" && <audio controls src={m.data_url} className="mt-1 w-full" />}
              <p className="stb-mono text-[10px] text-[#A3A3A3] mt-1">
                IP: <strong className="text-[#FF6B00]">{m.audit?.ip || "—"}</strong> ·{" "}
                UA: {(m.audit?.user_agent || "").slice(0, 100)}
              </p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

// ----------------------- NÁSTROJE (migrace fotek) ----------------------- //

function ToolsTab() {
  const [running, setRunning] = useState(false);
  const [status, setStatus] = useState("");
  const [done, setDone] = useState(false);
  const [expireBusy, setExpireBusy] = useState(false);
  const [expireMsg, setExpireMsg] = useState("");

  const migrate = async () => {
    setRunning(true); setDone(false);
    setStatus("Spouštím migraci fotek do úložiště…");
    try {
      let migratedTotal = 0;
      let guard = 0;
      while (guard < 1000) {
        guard += 1;
        const { data } = await api.post("/admin/migrate-photos");
        migratedTotal += data.migrated_now;
        setStatus(`Přesunuto inzerátů: ${migratedTotal} · zbývá: ${data.remaining}`);
        if (data.remaining === 0 || data.migrated_now === 0) break;
      }
      setStatus(`Hotovo! Fotky byly přesunuty do úložiště. Zpracováno inzerátů: ${migratedTotal}.`);
      setDone(true);
    } catch (e) {
      setStatus("Chyba: " + apiError(e));
    } finally {
      setRunning(false);
    }
  };

  return (
    <div data-testid="admin-tools-tab" className="max-w-2xl">
      <div className="stb-card-static p-5">
        <h3 className="stb-h text-lg text-white flex items-center gap-2">
          <Database size={18} className="text-[#FF6B00]" /> Migrace fotek do úložiště
        </h3>
        <p className="text-sm text-[#A3A3A3] mt-2 leading-relaxed">
          Přesune starší fotky uložené přímo v databázi (base64) do rychlého object storage.
          Zrychlí to načítání webu a odlehčí databázi. Akce je bezpečná a lze ji spustit
          opakovaně — přeskočí už přesunuté fotky.
        </p>
        <button
          onClick={migrate}
          disabled={running}
          className="stb-btn stb-btn-primary mt-4"
          data-testid="admin-migrate-photos-btn"
        >
          {running ? "Probíhá migrace…" : "Spustit migraci fotek"}
        </button>
        {status && (
          <p className={`mt-3 text-sm ${done ? "text-[#22C55E]" : "text-[#A3A3A3]"}`} data-testid="admin-migrate-status">
            {status}
          </p>
        )}
      </div>
      <div className="stb-card-static p-5 mt-4">
        <h3 className="stb-h text-lg text-white">Prošlé TOP inzeráty</h3>
        <p className="text-sm text-[#A3A3A3] mt-2 leading-relaxed">
          Odstraní příznak TOP u inzerátů, kterým už vypršelo top_until. Veřejný web je i bez toho
          přestane označovat jako TOP.
        </p>
        <button
          type="button"
          onClick={async () => {
            setExpireBusy(true);
            setExpireMsg("");
            try {
              const { data } = await api.post("/admin/expire-tops");
              setExpireMsg(`Hotovo. Označeno jako prošlé: ${data.expired ?? 0}.`);
            } catch (e) {
              setExpireMsg("Chyba: " + apiError(e));
            } finally {
              setExpireBusy(false);
            }
          }}
          disabled={expireBusy}
          className="stb-btn stb-btn-primary mt-4"
          data-testid="admin-expire-tops-btn"
        >
          {expireBusy ? "Probíhá…" : "Označit prošlé TOP"}
        </button>
        {expireMsg && (
          <p className="mt-3 text-sm text-[#A3A3A3]" data-testid="admin-expire-tops-status">{expireMsg}</p>
        )}
      </div>
    </div>
  );
}

// ----------------------- WINNERS (TOP soutěž) ----------------------- //

function WinnersTab() {
  const [items, setItems] = useState([]);
  const [topTotal, setTopTotal] = useState(0);
  const [err, setErr] = useState("");
  const [busyId, setBusyId] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/admin/top-winners");
      setItems(data.items);
      setTopTotal(data.top_total || 0);
    } catch (e) { setErr(apiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const markFulfilled = async (id) => {
    if (!window.confirm("Označit dárek jako odeslaný/vyřízený?")) return;
    setBusyId(id);
    try {
      await api.post(`/admin/top-winners/${id}/fulfill`);
      await load();
    } catch (e) { alert(apiError(e)); } finally { setBusyId(""); }
  };

  const toNext = topTotal % 100 === 0 ? (topTotal > 0 ? 100 : 100) : 100 - (topTotal % 100);

  return (
    <div data-testid="admin-winners-tab">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
        <div className="stb-card-static p-4">
          <p className="stb-label">Celkem TOPnutí</p>
          <p className="stb-h text-4xl text-[#FF6B00] mt-1">{topTotal}</p>
        </div>
        <div className="stb-card-static p-4">
          <p className="stb-label">Do dalšího dárku</p>
          <p className="stb-h text-4xl text-[#FFB800] mt-1">{toNext}</p>
        </div>
        <div className="stb-card-static p-4">
          <p className="stb-label">Výherců celkem</p>
          <p className="stb-h text-4xl text-white mt-1">{items.length}</p>
        </div>
      </div>

      {err && <p className="text-[#FF6B00]">{err}</p>}

      {items.length === 0 ? (
        <div className="stb-card-static p-6 text-center text-[#A3A3A3]">
          <Gift size={28} className="text-[#FF6B00] mx-auto mb-2" />
          Zatím žádní výherci. Jakmile počet TOPnutí dosáhne násobku 100, výherce se objeví zde.
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((w) => (
            <article key={w.id} className="stb-card-static p-3" data-testid={`admin-winner-row-${w.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="stb-h text-base text-white inline-flex items-center gap-2">
                    <Gift size={16} className="text-[#FF6B00]" /> {w.milestone}. TOPnutí
                    {w.fulfilled && (
                      <span className="stb-chip text-[#22C55E] border-[#22C55E]"><CheckCircle size={10} /> vyřízeno</span>
                    )}
                  </p>
                  <p className="text-sm mt-1">
                    <a href={`mailto:${w.winner_email}`} className="stb-mono text-[#FF6B00]">{w.winner_email || "—"}</a>
                  </p>
                  <p className="text-xs text-[#A3A3A3] mt-1">
                    Inzerát: {w.listing_id
                      ? <a href={`/inzerat/${w.listing_id}`} target="_blank" rel="noreferrer" className="stb-link">{w.listing_title || w.listing_id}</a>
                      : (w.listing_title || "—")}
                  </p>
                  <p className="stb-mono text-[10px] text-[#A3A3A3] mt-1">{shortDate(w.created_at)}</p>
                </div>
                {!w.fulfilled && (
                  <button
                    onClick={() => markFulfilled(w.id)}
                    disabled={busyId === w.id}
                    className="stb-btn stb-btn-secondary text-xs"
                    data-testid={`admin-winner-fulfill-${w.id}`}
                  >
                    <CheckCircle size={14} /> {busyId === w.id ? "…" : "Označit jako odeslané"}
                  </button>
                )}
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}

// ----------------------- helpers ----------------------- //

function Grid2({ items }) {
  return (
    <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 text-sm mt-2">
      {items.map(([k, v]) => (
        <div key={k} className="flex gap-2 py-1 border-b border-[#262626]/40">
          <dt className="stb-label whitespace-nowrap min-w-[8rem]">{k}</dt>
          <dd className="text-white text-sm break-all stb-mono">{v ?? "—"}</dd>
        </div>
      ))}
    </dl>
  );
}

function shortDate(s) {
  if (!s) return "";
  try { return new Date(s).toLocaleString("cs-CZ", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }); }
  catch { return s; }
}
